import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/pro-light-svg-icons/faEllipsis';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faPen } from '@fortawesome/pro-light-svg-icons/faPen';
import { faCode } from '@fortawesome/pro-light-svg-icons/faCode';
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash';
import { faFileCsv } from '@fortawesome/pro-light-svg-icons/faFileCsv';
import { faEyeSlash } from '@fortawesome/pro-light-svg-icons/faEyeSlash';
import { TypedMemo } from '../../common/types';
import { LockedControl } from '../../music24/components/LockedControl/LockedControl';
import { SMLUpgradeFeature } from '../../music24/config/music24PlanCapabilities';
import styles from './SMLPageMenu.module.scss';

interface SMLPageMenuProps {
  onDuplicate: () => void;
  onChangeAddress: () => void;
  /** Left out when the page has no embed to hand out — an unpublished one. */
  onEmbed?: () => void;
  /** Only on a pre-save page that has a campaign to export. */
  onExportPresaves?: () => void;
  /** Free cannot export fans. The item still opens — it opens the upgrade prompt instead. */
  isExportLocked?: boolean;
  /** Only on a published page, and never on the bio link. */
  onUnpublish?: () => void;
  /** Left out on the bio link, which the account cannot delete. */
  onDelete?: () => void;
}

/**
 * Everything the page can have done to it that is not "edit it" or "open it".
 *
 * The actions the artist reaches for daily are buttons; these are the monthly
 * ones, and they cost a click to reach on purpose. Delete sits alone under a
 * rule at the bottom, so the mouse never passes over it on the way to
 * something else.
 */
const SMLPageMenuComponent: React.FC<SMLPageMenuProps> = ({
  onDuplicate,
  onChangeAddress,
  onEmbed,
  onExportPresaves,
  isExportLocked = false,
  onUnpublish,
  onDelete,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen((open) => !open), []);

  return (
    <Dropdown isOpen={isOpen} toggle={toggle} direction="down">
      <DropdownToggle
        color="link"
        caret={false}
        className={styles.toggle}
        aria-label={intl.formatMessage({ id: 'app.sml.details.menu', defaultMessage: 'More actions' })}
      >
        <FontAwesomeIcon icon={faEllipsis} />
      </DropdownToggle>
      <DropdownMenu end className={styles.menu}>
        <div className={styles.group}>
          {intl.formatMessage({ id: 'app.sml.details.menu.page', defaultMessage: 'Page' })}
        </div>
        <DropdownItem className={styles.item} onClick={onDuplicate}>
          <FontAwesomeIcon icon={faCopy} />
          {intl.formatMessage({ id: 'app.sml.details.menu.duplicate', defaultMessage: 'Duplicate' })}
        </DropdownItem>
        <DropdownItem className={styles.item} onClick={onChangeAddress}>
          <FontAwesomeIcon icon={faPen} />
          {intl.formatMessage({ id: 'app.sml.details.menu.address', defaultMessage: 'Change address' })}
        </DropdownItem>
        {onEmbed !== undefined && (
          <DropdownItem className={styles.item} onClick={onEmbed}>
            <FontAwesomeIcon icon={faCode} />
            {intl.formatMessage({ id: 'app.sml.details.menu.embed', defaultMessage: 'Embed code' })}
          </DropdownItem>
        )}
        {onExportPresaves !== undefined && (
          <>
            <div className={styles.group}>
              {intl.formatMessage({ id: 'app.sml.details.menu.data', defaultMessage: 'Data' })}
            </div>
            <DropdownItem className={styles.item} onClick={onExportPresaves}>
              {isExportLocked ? (
                <LockedControl
                  variant="menuItem"
                  feature={SMLUpgradeFeature.fanExport}
                  label={intl.formatMessage({
                    id: 'app.sml.details.menu.export_presaves',
                    defaultMessage: 'Export pre-saves (CSV)',
                  })}
                />
              ) : (
                <>
                  <FontAwesomeIcon icon={faFileCsv} />
                  {intl.formatMessage({
                    id: 'app.sml.details.menu.export_presaves',
                    defaultMessage: 'Export pre-saves (CSV)',
                  })}
                </>
              )}
            </DropdownItem>
          </>
        )}
        {(onUnpublish !== undefined || onDelete !== undefined) && <DropdownItem divider className={styles.divider} />}
        {onUnpublish !== undefined && (
          <DropdownItem className={styles.item} onClick={onUnpublish}>
            <FontAwesomeIcon icon={faEyeSlash} />
            {intl.formatMessage({ id: 'app.sml.details.menu.unpublish', defaultMessage: 'Unpublish page' })}
          </DropdownItem>
        )}
        {onDelete !== undefined && (
          <DropdownItem className={styles.danger} onClick={onDelete}>
            <FontAwesomeIcon icon={faTrash} />
            {intl.formatMessage({ id: 'app.sml.details.menu.delete', defaultMessage: 'Delete page' })}
          </DropdownItem>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

export const SMLPageMenu = TypedMemo(SMLPageMenuComponent);
